import { BallFinalKinds, constants, Dexterity, IApiLineupPlayer, KOrBB, TopBottom } from '@trackman/baseball-shared-components';
import { IApiBallFinal } from 'interfaces/broadcast/IApiBallFinal';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { IApiStadiumBpPlayModel, IApiStadiumPlayModel } from 'interfaces/tagging/ApiModels/IApiStadiumPlayModel';
import { BpPlay } from 'models/tagging/BpPlay';
import { Lineup } from 'models/tagging/Lineup';
import moment from 'moment';
import { emptyGameState } from '../constants';
import mappingHelpers from './mappingHelpers';

export const apiPlayModelToBpPlayMap = (model: IApiStadiumPlayModel, ballFinals: IApiBallFinal[]) => {
  const play = new BpPlay(ballFinals[0]);
  play.id = model.id;
  play.playId = model.playId;
  play.playSequenceNumber = model.taggerBehavior.playSequenceNumber;
  play.sequenceNumber = model.taggerBehavior.sequenceNumber;
  play.isWarmup = model.taggerBehavior.isWarmup;
  play.batter = model.players.batter;
  play.isTagStored = true;
  play.isValidated = model.customMetadata.isValidated;
  play.isDeleted = model.taggerBehavior.isDeleted;
  play.note = model.note;
  play.playLineup = new Lineup(model.playLineup.homeTeam);
  play.ballFinals = ballFinals;
  return play;
};

export const bpPlayToApiPlayModelMap = (play: BpPlay, session: IApiSessionMetadataUpdated): IApiStadiumBpPlayModel => {
  const model = {
    id: play.id,
    playId: play.playId,
    sessionId: session.sessionId,
    taggerBehavior: bpPlayToApiTaggerBehaviorMap(play),
    players: {
      pitcher: { ...constants.emptyPlayer },
      batter: {
        ...play.batter,
        battingHandedness:
          play.batter.battingHandedness === Dexterity.Both ? Dexterity.Right : play.batter.battingHandedness || Dexterity.Undefined,
      },
      catcher: {
        ...constants.emptyPlayer,
        pitchingHandedness: Dexterity.Undefined,
      },
    },
    gameState: {
      ...emptyGameState,
      topBottom: TopBottom.Bottom,
      isHomeTeamBatting: true,
    },
    pitchTag: bpPlayToApiPitchTagMap(play),
    hitTag: bpPlayToApiHitTagMap(play),
    catcherThrowTag: { ...constants.emptyCatcherThrowTag },
    playResult: { ...constants.emptyPlayResult },
    note: play.note,
    kOrBB: KOrBB.Undefined,
    pitchingTeam: mappingHelpers.teamWithLineupToTeamMap(session.homeTeam),
    battingTeam: mappingHelpers.teamWithLineupToTeamMap(session.homeTeam),
    customMetadata: {
      isValidated: play.isValidated,
      isNoteResolved: play.isNoteResolved,
      taggingFlowState: '',
    },
    trackStartTimeUtc: play.trackStartTimeUtc,
    playLineup: {
      homeTeam: play.playLineup.players,
      awayTeam: [] as IApiLineupPlayer[],
    },
    createdInStadiumUtc: moment().toJSON(),
    createdInStadiumLocal: moment().toJSON(),
    updatedInStadiumUtc: moment().toJSON(),
    updatedInStadiumLocal: moment().toJSON(),
    time: moment().toJSON(),
    version: '0.0',
    systemId: session.systemId,
    trackStartTimeAutoGenerated: play.trackStartTimeAutoGenerated,
  } as IApiStadiumBpPlayModel;
  return model;
};

const bpPlayToApiPitchTagMap = (play: BpPlay) => {
  const bf = play.ballFinals.find((bf) => bf.kind === BallFinalKinds.Pitch);
  return {
    ...constants.emptyPitchTag,
    ballFinalTrackId: bf ? bf.trackId : undefined,
  };
};
const bpPlayToApiTaggerBehaviorMap = (play: BpPlay) => ({
  manuallyAddedByTagger: false,
  isWarmup: play.isWarmup,
  playSequenceNumber: play.playSequenceNumber,
  sequenceNumber: play.sequenceNumber,
  isDeleted: play.isDeleted,
});

const bpPlayToApiHitTagMap = (play: BpPlay) => {
  const bf = play.ballFinals.find((bf) => bf.kind === BallFinalKinds.Hit);
  return {
    ballFinalTrackId: bf ? bf.trackId : undefined,
    hitType: play.hitType,
    hitQuality: constants.undefinedValue,
  };
};
